((environment, createImageHelpers) => {
  'use strict';
  const imageHelpers = createImageHelpers();
  if (typeof module === 'object' && module.exports) module.exports = imageHelpers;
  if (!environment?.document) return;
  if (window.AlawooderHomeProductCards) {
    window.AlawooderHomeProductCards.init(document);
    return;
  }
  const mounted = new Map();

  function init(scope) {
    const cards = [...scope.querySelectorAll('.aw-home .aw-home-product-card')];
    if (scope.matches && scope.matches('.aw-home .aw-home-product-card')) cards.unshift(scope);
    cards.forEach(mount);
  }

  function mount(card) {
    if (mounted.has(card)) return;
    const controller = new AbortController();
    const { signal } = controller;
    let restoreImages = () => {};
    mounted.set(card, () => { controller.abort(); restoreImages(); mounted.delete(card); });
    mountQuickAdd(card, signal);
    const surface = card.querySelector('.aw-home-product-image-pair');
    const primary = card.querySelector('[data-home-primary-image]');
    const alternate = card.querySelector('[data-home-hover-image]');
    const caption = card.querySelector('[data-home-photo-caption]');
    if (!surface || !primary || !alternate) return;

    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const images = imageHelpers.createImageState({ primary, alternate, surface, caption, finePointer, baseURI: document.baseURI });
    restoreImages = images.destroy;

    surface.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      // Start fetching a lazy alternate immediately when a shopper reaches the photograph.
      alternate.loading = 'eager';
      images.requestHover(true);
    }, { signal });
    surface.addEventListener('pointerleave', () => images.requestHover(false), { signal });
    [primary, alternate].forEach(image => {
      image.addEventListener('load', images.sync, { signal });
      image.addEventListener('error', images.sync, { signal });
    });

    card.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-home-swatch]');
      if (!swatch || !card.contains(swatch) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
      images.select(swatch.dataset);
    }, { signal });

    finePointer.addEventListener('change', images.sync, { signal });
  }

  function mountQuickAdd(card, signal) {
    const form = card.querySelector('[data-home-quick-add]');
    if (!form) return;
    const button = form.querySelector('[data-aw-add]');
    const buttonText = form.querySelector('[data-home-add-text]');
    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const selection = form.querySelector('[data-home-add-selection]');
    const status = form.querySelector('[data-aw-form-status]');

    card.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-home-swatch]');
      if (!swatch || !card.contains(swatch) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
      const variantId = swatch.dataset.variantId || '';
      const available = /^\d+$/.test(variantId) && swatch.dataset.variantAvailable === 'true';
      const quantity = Math.max(1, Number(swatch.dataset.variantQuantity) || 1);
      const finish = swatch.dataset.label || '';
      const title = form.dataset.homeProductTitle;
      variantInput.value = variantId;
      quantityInput.value = String(quantity);
      button.dataset.awAvailable = String(available);
      button.disabled = !available || form.dataset.awBusy === 'true';
      buttonText.textContent = available ? (form.dataset.homeAddLabel || 'Add to bag') : (form.dataset.homeSoldLabel || 'Sold out');
      button.setAttribute('aria-label', available ? `Add ${title} in ${finish} to bag` : `${title} in ${finish} is sold out`);
      selection.textContent = `${finish} · ${form.dataset.homeQuantityLabel || 'Qty'} ${quantity}`;
      if (status && form.dataset.awBusy !== 'true') {
        status.textContent = '';
        status.hidden = true;
      }
    }, { signal });
  }

  function destroy(scope) {
    mounted.forEach((cleanup, card) => {
      if (scope === card || scope.contains(card)) cleanup();
    });
  }
  window.AlawooderHomeProductCards = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})(typeof window === 'undefined' ? null : window, () => {
  function imageKey(value, baseURI) {
    if (!value || !String(value).trim()) return '';
    try {
      const url = new URL(value, baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) return '';
      // Responsive widths are the same photograph; preserve version and other identity parameters.
      ['width', 'height', 'crop', 'quality', 'format', 'pad_color', 'dpr'].forEach(key => url.searchParams.delete(key));
      url.searchParams.sort();
      url.hash = '';
      return url.href;
    } catch (_) { return ''; }
  }
  function imageReady(image, expected, baseURI) {
    return Boolean(expected && image.complete && image.naturalWidth > 0 && imageKey(image.currentSrc || image.getAttribute('src'), baseURI) === expected);
  }
  function setDimension(image, name, value) {
    const number = Number(value);
    if (Number.isFinite(number) && number > 0) image.setAttribute(name, String(number));
    else image.removeAttribute(name);
  }
  function createImageState({ primary, alternate, surface, caption, finePointer, baseURI }) {
    let expectedPrimary = imageKey(primary.getAttribute('src'), baseURI);
    let expectedAlternate = imageKey(alternate.getAttribute('src'), baseURI);
    let primaryCaption = caption?.textContent.trim() || '';
    let requested = false;
    let selected = false;
    const originalVisibility = primary.style.visibility;
    const originalAlternateVisibility = alternate.style.visibility;

    function sync() {
      const visible = requested && finePointer.matches && imageReady(alternate, expectedAlternate, baseURI);
      surface.classList.toggle('is-showing-alternate', Boolean(visible));
      // While a new primary loads, keep the previous finish's decoded pixels out of view.
      if (selected) {
        primary.style.visibility = imageReady(primary, expectedPrimary, baseURI) ? originalVisibility : 'hidden';
        // Opacity transitions alone would leave the old finish fading for 320ms after a swatch click.
        alternate.style.visibility = imageReady(alternate, expectedAlternate, baseURI) ? originalAlternateVisibility : 'hidden';
      }
      if (caption) caption.textContent = visible ? (alternate.dataset.hoverCaption || primaryCaption) : primaryCaption;
    }

    function select(data) {
      requested = false;
      selected = true;
      expectedPrimary = imageKey(data.image, baseURI);
      expectedAlternate = imageKey(data.hoverImage, baseURI);
      primaryCaption = data.caption || (data.label ? `${data.label} finish shown.` : '');
      // Invalidate the old color before setting any source. Late load/error events only recheck current URLs.
      sync();
      if (data.imageSrcset && expectedPrimary) primary.setAttribute('srcset', data.imageSrcset);
      else primary.removeAttribute('srcset');
      if (data.imageSizes && expectedPrimary) primary.setAttribute('sizes', data.imageSizes);
      else primary.removeAttribute('sizes');
      setDimension(primary, 'width', data.imageWidth);
      setDimension(primary, 'height', data.imageHeight);
      primary.alt = data.alt || '';
      primary.loading = 'eager';
      if (expectedPrimary) primary.setAttribute('src', data.image);
      else primary.removeAttribute('src');

      alternate.removeAttribute('srcset');
      alternate.removeAttribute('sizes');
      setDimension(alternate, 'width', data.hoverWidth);
      setDimension(alternate, 'height', data.hoverHeight);
      alternate.alt = data.hoverAlt || '';
      alternate.dataset.hoverCaption = data.hoverCaption || '';
      if (expectedAlternate) alternate.setAttribute('src', data.hoverImage);
      else alternate.removeAttribute('src');
      sync();
    }

    function requestHover(value) { requested = value; sync(); }
    function destroy() {
      surface.classList.toggle('is-showing-alternate', false);
      primary.style.visibility = originalVisibility;
      alternate.style.visibility = originalAlternateVisibility;
    }
    return { select, sync, requestHover, destroy };
  }
  return { imageKey, imageReady, createImageState };
});
